<template>
	<div class="viewport-panel">
		<div class="header">
			<div class="title-row">
				<div class="heading">
					<span class="title">{{
						t("opportunities.viewportPanel.title", { count: displayedTotalCount }, displayedTotalCount)
					}}</span>
					<span v-if="projectName" class="subtitle">· {{ projectName }}</span>
				</div>
				<button
					type="button"
					class="icon-button collapse-btn"
					:title="t('callToActions.close')"
					:aria-label="t('filters.close')"
					@click="emit('close')"
				>
					<AppIcon :name="IconName.CloseSmall" :size="18" :stroke-width="1.2" />
				</button>
			</div>
			<p v-if="clusteredCount > 0" class="clustered-hint">
				{{ t("opportunities.viewportPanel.clusteredHint", { count: clusteredCount }, clusteredCount) }}
			</p>
		</div>

		<div class="content">
			<p v-if="opportunities.length === 0 && clusteredCount === 0" class="empty">
				{{ t(noOpportunityLayerActive ? "opportunities.viewportPanel.noLayer" : "opportunities.viewportPanel.empty") }}
			</p>
			<opportunity-card-summary
				v-for="opportunity in opportunities"
				:key="opportunity.id"
				:opportunity="opportunity"
				@select="emit('select', opportunity)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { Opportunity } from "@/model/DataModel.ts"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import OpportunityCardSummary from "@/components/opportunities/OpportunityCardSummary.vue"

const props = defineProps<{
	opportunities: Opportunity[]
	totalCount: number
	projectName?: string
	noOpportunityLayerActive?: boolean
}>()

const emit = defineEmits<{
	close: []
	select: [opportunity: Opportunity]
}>()

const { t } = useI18n()

const displayedTotalCount = computed(() => Math.max(props.totalCount, props.opportunities.length))
const clusteredCount = computed(() => displayedTotalCount.value - props.opportunities.length)
</script>

<style scoped>
.viewport-panel {
	position: fixed;
	top: 0;
	right: 0;
	z-index: 5;
	width: var(--width-viewport-opportunities-panel);
	height: 100%;
	display: flex;
	flex-direction: column;
	background-color: var(--neutral-0);
	box-shadow: var(--shadow-base-right);
	overflow: hidden;

	.header {
		flex-shrink: 0;
		padding: var(--spacing-md);
		border-bottom: 1px solid var(--neutral-100);

		.title-row {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: var(--spacing-sm);
		}

		.title {
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-semibold);
		}

		.heading {
			display: flex;
			flex-wrap: wrap;
			align-items: baseline;
			gap: var(--spacing-xs);
			min-width: 0;
		}

		.subtitle {
			color: var(--neutral-600);
			font-size: var(--font-size-xs);
		}

		.clustered-hint {
			margin: var(--spacing-sm) 0 0;
			padding: var(--spacing-sm);
			border-radius: var(--border-radius-sm);
			border: var(--border-width-thin) solid var(--neutral-100);
			background: var(--neutral-50);
			color: var(--neutral-700);
			font-size: var(--font-size-xs);
		}

		.icon-button {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			padding: var(--spacing-sm);
			border: none;
			border-radius: var(--border-radius-sm);
			background: none;
			color: var(--neutral-600);
			cursor: pointer;
			transition: all 0.2s ease;

			&:hover {
				background: var(--neutral-100);
				color: var(--neutral-900);
			}
		}
	}

	.content {
		flex: 1;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		padding: var(--spacing-md);

		.empty {
			color: var(--neutral-600);
			font-size: var(--font-size-xs);
			text-align: center;
			margin: var(--spacing-lg) 0;
		}
	}
}
</style>
